import { IConversationSummary, IConversationMessage, IConversationsPage, IConversationMessagesPage } from '../interfaces/heavyDutyMachinery/IConversation';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** `before` is the id of the last IConversationSummary already loaded (omit for the first page); `limit` defaults server-side. */
export const getMyConversations = async (before?: string, limit?: number): Promise<IConversationsPage> => {
  const params: Record<string, string | number> = {};
  if (before) params.before = before;
  if (limit) params.limit = limit;
  const response = await apiService<IConversationsPage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_CONVERSATIONS_MINE,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load conversations');
};

export const getConversationByBooking = async (bookingId: string): Promise<IConversationSummary> => {
  const response = await apiService<IConversationSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_CONVERSATION_BY_BOOKING(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load conversation');
};

/** `before` is the id of the oldest IConversationMessage already loaded (omit for the first/newest page); `limit` defaults server-side. Items always come back chronological (oldest first) regardless of page. */
export const getConversationMessages = async (conversationId: string, before?: number, limit?: number): Promise<IConversationMessagesPage> => {
  const params: Record<string, string | number> = {};
  if (before != null) params.before = before;
  if (limit) params.limit = limit;
  const response = await apiService<IConversationMessagesPage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_CONVERSATION_MESSAGES(conversationId),
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load messages');
};

/** Soft-hides the conversation for the caller only (WhatsApp-style: reappears automatically once a new message arrives) -- never deletes anything server-side. */
export const hideConversation = async (conversationId: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_CONVERSATION_HIDE(conversationId),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to hide conversation');
};

export const sendConversationMessage = async (conversationId: string, text: string): Promise<IConversationMessage> => {
  const response = await apiService<IConversationMessage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_CONVERSATION_MESSAGES(conversationId),
    method: HTTP_METHODS.POST,
    body: { text },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to send message');
};
